import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService, { AdminUser } from '../services/authService';
import {
  notaLogin,
  notaLogout,
  notaRequestPasswordReset,
  notaResetPassword,
  notaChangePassword,
  NotaPasswordResult,
} from '../services/notaAuthService';
import { fetchCurrentUser } from '../services/meService';
import { isSsoEnabled } from '../config/ssoConfig';
import { isSsoSession, initiateSsoLogin, ssoLogout, bridgeTokens, clearSsoSession } from '../services/ssoService';

// AdminUser interface is now imported from authService

interface AuthContextType {
  isAuthenticated: boolean;
  user: AdminUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  ssoLogin: (from?: string) => Promise<void>;
  completeSsoLogin: (user: AdminUser) => void;
  logout: (logoutAll?: boolean) => Promise<void>;
  forceLogout: () => void;
  isAdmin: () => boolean;
  isSystemAdmin: () => boolean;
  isVenueUser: () => boolean;
  canAccessVenue: (venueId: string) => boolean;
  canViewStockCosts: () => boolean;
  passwordChangeRequired: boolean;
  forgotPassword: (email: string) => Promise<{ message: string }>;
  resetPassword: (token: string, newPassword: string) => Promise<{ message: string }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ message: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Global logout handler for use outside React components (e.g., in api.ts)
let globalForceLogout: (() => void) | null = null;

export const setGlobalLogoutHandler = (handler: () => void) => {
  globalForceLogout = handler;
};

export const getGlobalLogoutHandler = () => globalForceLogout;

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);

  // Force logout without calling API - used when token refresh fails
  const forceLogout = () => {
    if (isSsoSession()) {
      clearSsoSession();
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiry');
    localStorage.removeItem('user');
    localStorage.removeItem('password_change_required');

    setUser(null);
    setPasswordChangeRequired(false);
    setLoading(false);
  };

  // Set the global logout handler for use in api.ts
  React.useEffect(() => {
    setGlobalLogoutHandler(forceLogout);
    
    // Cleanup on unmount
    return () => {
      setGlobalLogoutHandler(() => {});
    };
  }, []);

  useEffect(() => {
    // Check if user is already logged in and refresh token if needed.
    //
    // NOTE on dispatch: bootstrap behaviour for the LOCAL path is unchanged.
    // For the NOTA path the proactive 5-minute refresh window doesn't apply
    // (we don't store `token_expiry` for Nota tokens — see
    // notaAuthService.persistTokens), so bootstrap simply trusts the stored
    // token and lets the api.ts response interceptor handle a 401 by
    // refreshing reactively. This is intentional: the Nota flow rotates BOTH
    // tokens, so trying to be clever pre-expiry just risks a wasted rotation
    // pair.
    const checkAuth = async () => {
      try {
        // ── SSO path ─────────────────────────────────────────────────
        if (isSsoEnabled() && isSsoSession()) {
          try {
            bridgeTokens();
            const cachedUser = authService.getCurrentUser();
            if (cachedUser) setUser(cachedUser);

            const fresh = await fetchCurrentUser();
            localStorage.setItem('user', JSON.stringify(fresh));
            setUser(fresh);
          } catch (ssoErr) {
            console.error('SSO /admin/me hydration failed; clearing SSO session.', ssoErr);
            clearSsoSession();
          } finally {
            setLoading(false);
          }
          return;
        }

        const currentUser = authService.getCurrentUser();

        // ── Rehydrate from nota-main-api ────────────────────────────────
        // If we have a token, rehydrate from /admin/me. This covers both
        // the "user is missing" case (rare but possible if localStorage was
        // partially cleared) and the "user might be stale" case (role or
        // venue assignments changed since last login). Hydration uses the
        // existing access_token; the api.ts response interceptor will
        // transparently refresh it if expired.
        const hasToken = !!localStorage.getItem('access_token');
        if (!hasToken) {
          // No session to restore.
          return;
        }
        // Optimistic UI: if we have a cached user, render it immediately so
        // the app doesn't flash the login screen on every reload.
        if (currentUser) setUser(currentUser);

        try {
          const fresh = await fetchCurrentUser();
          localStorage.setItem('user', JSON.stringify(fresh));
          setUser(fresh);
        } catch (hydrationError) {
          // Token can't be exchanged for a user — session is unusable.
          console.error('Nota /admin/me hydration failed; signing out.', hydrationError);
          forceLogout();
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // If auth check fails, force logout to clear any inconsistent state
        forceLogout();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      // Only the `success` Nota status is supported. Other statuses
      // (2fa-required / passkey-required / setup-required / failed) surface as
      // thrown errors with a clear message — the matching screens are out of
      // scope and will be added on top of this without changing it.
      const result = await notaLogin(email, password);
      if (result.kind === 'success') {
        // notaLogin persisted the tokens. Now hydrate the AdminUser from
        // wine-api `/admin/me` — Nota tokens carry only identity, not
        // role/assignedVenueIds. If hydration fails (e.g. unmapped Nota
        // user → 403 NOTA_USER_NOT_MAPPED, or wine-api unreachable), we
        // refuse to enter the app: clear the tokens we just stored and
        // surface the error to LoginPage.
        let realUser;
        try {
          realUser = await fetchCurrentUser();
        } catch (hydrationError: any) {
          // Roll back the half-completed login.
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('token_expiry');
          localStorage.removeItem('user');
          const status = hydrationError?.response?.status;
          const code = hydrationError?.response?.data?.code;
          if (status === 403 && code === 'NOTA_USER_NOT_MAPPED') {
            throw new Error(
              'This Nota account is not authorized for the wine app. Ask an administrator to add your email to the wine-api admin user list.',
            );
          }
          throw new Error(
            `Could not load your profile after sign-in (${status ?? 'network error'}). Please try again.`,
          );
        }
        // Persist + commit so AuthContext bootstrap finds it after refresh.
        localStorage.setItem('user', JSON.stringify(realUser));
        setUser(realUser);
        // Nota does not carry the local "passwordChangeRequired" concept —
        // password lifecycle moves to Nota's own flows in a future step.
        setPasswordChangeRequired(false);
        localStorage.removeItem('password_change_required');
        return;
      }
      // All non-success kinds: throw so the LoginPage's existing catch
      // branch surfaces a user-visible error. We intentionally do NOT
      // partially log the user in.
      const err = new Error(result.message);
      (err as any).notaKind = result.kind;
      if (result.kind === 'failed' && (result as any).errorCode) {
        (err as any).notaErrorCode = (result as any).errorCode;
      }
      throw err;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const ssoLogin = async (from?: string) => {
    await initiateSsoLogin(from);
  };

  const completeSsoLogin = (ssoUser: AdminUser) => {
    setUser(ssoUser);
    setPasswordChangeRequired(false);
    localStorage.removeItem('password_change_required');
  };

  const logout = async (logoutAll: boolean = false) => {
    if (isSsoEnabled() && isSsoSession()) {
      try {
        ssoLogout();
      } catch (err) {
        console.error('SSO logout failed; clearing local session.', err);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('token_expiry');
        localStorage.removeItem('user');
        localStorage.removeItem('sso_session');
        localStorage.removeItem('password_change_required');
        window.location.href = '/login';
      }
      return;
    }

    // main-api's logout has no `allDevices` concept — `logoutAll` is accepted
    // for signature compatibility but ignored. Tokens are rotated on every
    // refresh, so a one-device logout already invalidates that device's chain.
    await notaLogout();
    setUser(null);
    setPasswordChangeRequired(false);
    localStorage.removeItem('password_change_required');
    window.location.href = '/login';
  };

  const isAdmin = () => {
    return authService.isAdmin();
  };

  const isSystemAdmin = () => {
    return authService.isSystemAdmin();
  };

  const isVenueUser = () => {
    return authService.isVenueUser();
  };

  const canAccessVenue = (venueId: string) => {
    return authService.canAccessVenue(venueId);
  };

  // COSTING.1d — cost visibility (display-gating; server strips authoritatively).
  const canViewStockCosts = () => {
    return authService.canViewStockCosts();
  };

  // Password management — all against nota-main-api.
  //
  // notaAuthService returns a discriminated result rather than throwing, so
  // failures are converted to a thrown Error here to preserve the contract the
  // existing callers already expect.
  const unwrapNotaPassword = (result: NotaPasswordResult, successMessage: string) => {
    if (result.kind === 'failed') throw new Error(result.message);
    return { message: successMessage };
  };

  const forgotPassword = async (email: string) => {
    return unwrapNotaPassword(
      await notaRequestPasswordReset(email),
      'If that email is registered, a reset link is on its way.',
    );
  };

  const resetPassword = async (token: string, newPassword: string) => {
    return unwrapNotaPassword(
      await notaResetPassword(token, newPassword),
      'Password reset. You can sign in with your new password.',
    );
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    if (!user) throw new Error('User not authenticated');
    // main-api derives the user from the bearer token, so no id is passed.
    const result = unwrapNotaPassword(
      await notaChangePassword(currentPassword, newPassword),
      'Password changed.',
    );

    // If password was changed successfully and it was required, clear the flag
    if (passwordChangeRequired) {
      setPasswordChangeRequired(false);
      localStorage.removeItem('password_change_required');
      
      // Update the user object to reflect that password change is no longer required
      if (user) {
        const updatedUser = { ...user, passwordChangeRequired: false };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    }
    
    return result;
  };

  // Session listing/revocation is deliberately absent.
  //
  // It has no main-api equivalent — main-api exposes only
  // `PUT /admin/device-session` (update), with no list and no revoke — so
  // unlike the password operations above it cannot be forwarded. Sessions are
  // managed in the Nota admin console.
  //
  // Removed rather than stubbed: a method that always throws, or that returns
  // an empty list, would read to a caller as "you have no other sessions",
  // which is a security-relevant lie.

  const value = {
    isAuthenticated: !!user,
    user,
    loading,
    login,
    ssoLogin,
    completeSsoLogin,
    logout,
    forceLogout,
    isAdmin,
    isSystemAdmin,
    isVenueUser,
    canAccessVenue,
    canViewStockCosts,
    passwordChangeRequired,
    forgotPassword,
    resetPassword,
    changePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
